/**
 * Client-side analytics logging utilities
 * Sends events to Cloudflare Functions which log to Azure Table Storage
 */

export interface CalculatorUsageEvent {
  calculatorName: string;
  calculatorSlug: string;
  category: string;
  timestamp?: string;
}

export interface SearchEvent {
  query: string;
  resultsCount: number;
  timestamp?: string;
}

export interface ErrorEvent {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent?: string;
  timestamp?: string;
}

/**
 * Log calculator usage event
 */
export async function logCalculatorUsage(event: CalculatorUsageEvent): Promise<void> {
  try {
    await fetch('/api/log-calculator', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...event,
        timestamp: event.timestamp || new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('Failed to log calculator usage:', error);
    // Silently fail - don't disrupt user experience
  }
}

/**
 * Log search query
 */
export async function logSearch(event: SearchEvent): Promise<void> {
  try {
    await fetch('/api/log-search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...event,
        timestamp: event.timestamp || new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('Failed to log search:', error);
    // Silently fail - don't disrupt user experience
  }
}

/**
 * Log JavaScript error
 */
export async function logError(event: ErrorEvent): Promise<void> {
  try {
    await fetch('/api/log-error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...event,
        timestamp: event.timestamp || new Date().toISOString(),
        userAgent: event.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
      }),
    });
  } catch (error) {
    console.error('Failed to log error:', error);
    // Silently fail - don't disrupt user experience
  }
}

// ============================================
// GA4 EVENT TRACKING
// ============================================

/**
 * GA4 Analytics Event Tracking
 *
 * Events designed to support funnel analysis for SEO conversion tracking.
 *
 * Funnel stages:
 * 1. Landing (automatic via page_view)
 * 2. Engagement (scroll, time on page, interactions)
 * 3. Micro-conversion (tool usage, code copy, content interaction)
 * 4. Conversion (contact form, CTA clicks)
 */

// Type declarations for gtag, Zaraz, and Clarity
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      action: string,
      params?: Record<string, unknown>
    ) => void;
    zaraz?: {
      track: (eventName: string, properties?: Record<string, unknown>) => void;
    };
    dataLayer?: unknown[];
    clarity?: (
      command: 'set' | 'identify' | 'event' | 'upgrade',
      ...args: unknown[]
    ) => void;
  }
}

/**
 * Check if Zaraz is available (Cloudflare tag manager)
 */
function isZarazAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.zaraz && typeof window.zaraz.track === 'function';
}

/**
 * Check if gtag is available
 */
function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Safe wrapper for GA4 event calls
 *
 * For 'event' commands: prefers zaraz.track (sends to GA4 via Zaraz server-side pipeline)
 * over window.gtag (which only reaches Google Ads via Google Tag Gateway).
 *
 * The Google Tag Gateway's dataLayer is configured with AW-* (Google Ads) only,
 * not the GA4 measurement ID (G-JVH7G88W49). Zaraz's server-side integration
 * handles GA4, so custom events must go through zaraz.track to reach GA4.
 *
 * For 'config'/'set' commands: uses gtag directly (these configure the tag, not send events).
 */
function safeGtag(
  command: 'event' | 'config' | 'set',
  action: string,
  params?: Record<string, unknown>
): void {
  if (command === 'event') {
    // Prefer zaraz.track for events — it routes through Zaraz's server-side GA4 pipeline
    if (isZarazAvailable()) {
      window.zaraz!.track(action, params);
    } else if (isGtagAvailable()) {
      window.gtag!(command, action, params);
    }
  } else {
    // config/set commands go to gtag directly
    if (isGtagAvailable()) {
      window.gtag!(command, action, params);
    }
  }
}

// ============================================
// MICROSOFT CLARITY TRACKING
// ============================================

/**
 * Check if Clarity is available
 */
function isClarityAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.clarity === 'function';
}

/**
 * Set a custom tag in Clarity for session segmentation
 * Tags help filter and segment session recordings
 */
export function claritySet(key: string, value: string): void {
  if (isClarityAvailable()) {
    window.clarity!('set', key, value);
  }
}

/**
 * Fire a custom event in Clarity
 * Events are used to mark important moments in recordings
 */
export function clarityEvent(eventName: string): void {
  if (isClarityAvailable()) {
    window.clarity!('event', eventName);
  }
}

/**
 * Identify a user in Clarity (after form submission)
 * Links session recordings to a specific user
 * @param customId - unique identifier (email or user ID)
 * @param customSessionId - optional session identifier
 * @param customPageId - optional page identifier
 */
export function clarityIdentify(
  customId: string,
  customSessionId?: string,
  customPageId?: string
): void {
  if (isClarityAvailable()) {
    window.clarity!('identify', customId, customSessionId, customPageId);
  }
}

/**
 * Upgrade the current session to always be recorded
 * Use for high-value sessions (e.g., users who submit forms)
 */
export function clarityUpgrade(reason: string): void {
  if (isClarityAvailable()) {
    window.clarity!('upgrade', reason);
  }
}

// ============================================
// ENGAGEMENT EVENTS (Stage 2)
// ============================================

/**
 * Track when user scrolls to a significant portion of the page
 */
export function trackScrollDepth(percentage: number): void {
  safeGtag('event', 'scroll_depth', {
    event_category: 'engagement',
    event_label: `${percentage}%`,
    value: percentage,
  });
}

/**
 * Track content engagement (reading time threshold)
 */
export function trackContentEngagement(
  contentType: 'blog' | 'tool' | 'knowledge_base' | 'service',
  slug: string,
  timeSpentSeconds: number
): void {
  safeGtag('event', 'content_engagement', {
    event_category: 'engagement',
    content_type: contentType,
    content_slug: slug,
    time_spent: timeSpentSeconds,
    engaged: timeSpentSeconds >= 30, // 30+ seconds = engaged
  });
}

// ============================================
// MICRO-CONVERSION EVENTS (Stage 3)
// ============================================

/**
 * Track code/content copy actions
 */
export function trackCopy(
  copyType: 'code_block' | 'inline_code' | 'tool_result' | 'color' | 'config',
  contentSlug?: string
): void {
  safeGtag('event', 'copy_content', {
    event_category: 'micro_conversion',
    copy_type: copyType,
    content_slug: contentSlug || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
}

/**
 * Track engagement with an in-article hero tool embed (collapsible widget).
 * Lets us measure whether readers actually use the embedded tool vs. scroll past
 * it: 'view' fires when the embed is on the page, expand/collapse on toggle.
 */
export function trackToolEmbed(
  toolSlug: string,
  action: 'view' | 'expand' | 'collapse',
  pageSlug?: string
): void {
  safeGtag('event', 'tool_embed_engagement', {
    event_category: 'micro_conversion',
    tool_slug: toolSlug,
    embed_action: action,
    page_slug: pageSlug || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });

  // Clarity: segment readers who engage an embedded tool
  if (action === 'expand' || action === 'view') {
    claritySet('tool_embed_viewer', toolSlug);
  }
}

/**
 * Track tool usage/completion
 */
export function trackToolUsage(
  toolSlug: string,
  action: 'start' | 'interact' | 'complete' | 'result_generated' | 'export'
): void {
  safeGtag('event', 'tool_usage', {
    event_category: 'micro_conversion',
    tool_slug: toolSlug,
    tool_action: action,
  });

  // Clarity: Tag tool users for segmentation
  claritySet('tool_user', toolSlug);
  if (action === 'result_generated' || action === 'complete') {
    claritySet('tool_completed', toolSlug);
    clarityEvent(`tool_${action}`);
  }
}

/**
 * Track FAQ expansion (shows content interest)
 */
export function trackFaqExpand(question: string, pageSlug: string): void {
  safeGtag('event', 'faq_expand', {
    event_category: 'micro_conversion',
    faq_question: question.substring(0, 100), // Truncate for GA4 limits
    page_slug: pageSlug,
  });
}

/**
 * Track related content clicks
 */
export function trackRelatedContentClick(
  fromPage: string,
  toPage: string,
  contentType: 'tool' | 'blog' | 'service' | 'knowledge_base'
): void {
  safeGtag('event', 'related_content_click', {
    event_category: 'micro_conversion',
    from_page: fromPage,
    to_page: toPage,
    content_type: contentType,
  });
}

/**
 * Track search usage (GA4 version)
 */
export function trackSearchGA4(query: string, resultsCount: number): void {
  safeGtag('event', 'search', {
    event_category: 'micro_conversion',
    search_term: query,
    results_count: resultsCount,
  });
}

/**
 * Track search result click
 */
export function trackSearchResultClick(query: string, clickedUrl: string, position: number): void {
  safeGtag('event', 'search_result_click', {
    event_category: 'micro_conversion',
    search_term: query,
    clicked_url: clickedUrl,
    position: position,
  });
}

// ============================================
// CONVERSION EVENTS (Stage 4)
// ============================================

// Track CTA button clicks.
// Re-exported rather than defined here. The blog and tools workers cannot
// import this 1,100-line file, so the CTA tracking they share lives in a file
// small enough to copy — see lib/analytics-cta.ts. Keeping a second copy of the
// function here is how the two would drift.
export { trackCtaClick, type CtaLocation } from './analytics-cta';

/**
 * Track contact form interactions
 */
export function trackContactForm(
  action: 'view' | 'start' | 'field_focus' | 'submit' | 'success' | 'error',
  formLocation?: string,
  errorMessage?: string
): void {
  safeGtag('event', 'contact_form', {
    event_category: 'conversion',
    form_action: action,
    form_location: formLocation || (typeof window !== 'undefined' ? window.location.pathname : ''),
    ...(errorMessage && { error_message: errorMessage }),
  });
}

/**
 * Track outbound link clicks (to external resources)
 */
export function trackOutboundClick(url: string, linkText: string): void {
  safeGtag('event', 'outbound_click', {
    event_category: 'conversion',
    outbound_url: url,
    link_text: linkText,
  });
}

/**
 * Track affiliate-offer clicks (revenue events). Mark `affiliate_click` as a
 * conversion in GA4 admin so we can see which tool -> offer pairs actually earn.
 */
export function trackAffiliateClick(
  offerName: string,
  toolSlug: string,
  destinationUrl: string
): void {
  safeGtag('event', 'affiliate_click', {
    event_category: 'conversion',
    affiliate_offer: offerName,
    tool_slug: toolSlug,
    destination_url: destinationUrl,
  });
  // Segment Clarity sessions that engaged an offer (high-value behaviour).
  claritySet('affiliate_offer', offerName);
  clarityEvent('affiliate_click');
}

/**
 * Track lead form submission (GA4 recommended event: generate_lead)
 * This is a key conversion event - mark as conversion in GA4 admin
 * Automatically includes attribution data (landing page, visit count, days to convert)
 */
export function trackLeadFormSubmission(
  formName: string,
  formLocation: string,
  pageUrl?: string,
  email?: string
): void {
  // Get attribution data
  const landingPage = typeof window !== 'undefined' ? sessionStorage.getItem('ihq_landing_page') || '' : '';
  const visitCount = typeof window !== 'undefined' ? parseInt(localStorage.getItem('ihq_visit_count') || '1', 10) : 1;
  const firstVisit = typeof window !== 'undefined' ? localStorage.getItem('ihq_first_visit') : null;
  const daysToConvert = firstVisit ? Math.floor((Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const referrer = typeof window !== 'undefined' ? sessionStorage.getItem('ihq_referrer') || '' : '';

  // GA4 recommended event for lead generation with attribution
  safeGtag('event', 'generate_lead', {
    event_category: 'conversion',
    form_name: formName,
    form_location: formLocation,
    page_url: pageUrl || (typeof window !== 'undefined' ? window.location.href : ''),
    // Attribution data
    landing_page: landingPage,
    visit_count: visitCount,
    days_to_convert: daysToConvert,
    referrer_domain: referrer ? (() => { try { return new URL(referrer).hostname; } catch { return 'direct'; } })() : 'direct',
    is_return_visitor: visitCount > 1,
    currency: 'USD',
    value: 1, // Assign a value for ROI calculations
  });

  // Clarity: Tag session and identify user with full attribution
  claritySet('lead_submitted', 'true');
  claritySet('lead_form', formName);
  claritySet('lead_source', formLocation);
  claritySet('lead_landing_page', landingPage);
  claritySet('lead_visit_count', visitCount.toString());
  claritySet('lead_days_to_convert', daysToConvert.toString());
  clarityEvent('lead_form_submitted');
  clarityUpgrade('lead_submission');

  // If email provided, identify the user for session linking
  if (email) {
    clarityIdentify(email);
  }
}

/**
 * Track click to HubSpot meeting scheduler
 * High-intent conversion action - mark as conversion in GA4 admin
 */
export function trackScheduleMeetingClick(
  source: string,
  schedulerUrl?: string
): void {
  safeGtag('event', 'schedule_meeting_click', {
    event_category: 'conversion',
    click_source: source,
    scheduler_url: schedulerUrl || 'hubspot_meetings',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    currency: 'USD',
    value: 5, // Higher value - shows strong intent
  });

  // Clarity: Tag high-intent session
  claritySet('schedule_meeting_clicked', 'true');
  claritySet('meeting_source', source);
  clarityEvent('schedule_meeting_click');
  clarityUpgrade('meeting_scheduler_click');
}

// ============================================
// PAGE-SPECIFIC EVENTS
// ============================================

/**
 * Track service page interest
 */
export function trackServiceInterest(
  serviceSlug: string,
  action: 'view_features' | 'view_pricing' | 'view_faq' | 'scroll_to_cta'
): void {
  safeGtag('event', 'service_interest', {
    event_category: 'engagement',
    service_slug: serviceSlug,
    interest_action: action,
  });
}

/**
 * Track blog post completion
 */
export function trackBlogCompletion(postSlug: string, readTimeMinutes: number): void {
  safeGtag('event', 'blog_complete', {
    event_category: 'micro_conversion',
    post_slug: postSlug,
    read_time: readTimeMinutes,
  });
}

// ============================================
// PHONE & DIRECT CONTACT TRACKING
// ============================================

/**
 * Track phone number clicks (tel: links)
 * High-intent action - mobile users calling directly
 */
export function trackPhoneClick(
  source: string,
  phoneNumber?: string
): void {
  safeGtag('event', 'phone_click', {
    event_category: 'conversion',
    click_source: source,
    phone_number: phoneNumber || '(866) 903-2097',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    currency: 'USD',
    value: 10, // Very high intent - they're calling
  });

  // Clarity: Tag high-intent phone session
  claritySet('phone_clicked', 'true');
  claritySet('phone_source', source);
  clarityEvent('phone_click');
  clarityUpgrade('phone_call_intent');
}

// ============================================
// FORM INTERACTION TRACKING
// ============================================

/**
 * Track form field focus - indicates form engagement started
 * Only fire once per form per session
 */
export function trackFormStart(
  formName: string,
  formLocation: string
): void {
  safeGtag('event', 'form_start', {
    event_category: 'engagement',
    form_name: formName,
    form_location: formLocation,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
  });
}

/**
 * Track form abandonment - user started but didn't submit
 * Call this on page unload if form was started but not submitted
 */
export function trackFormAbandonment(
  formName: string,
  formLocation: string,
  lastFieldFocused?: string
): void {
  safeGtag('event', 'form_abandonment', {
    event_category: 'engagement',
    form_name: formName,
    form_location: formLocation,
    last_field: lastFieldFocused,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
  });
}

// ============================================
// EXIT INTENT TRACKING
// ============================================

/**
 * Track exit intent popup interactions
 * Understand how users respond to exit intent
 */
export function trackExitIntent(
  action: 'shown' | 'dismissed_x' | 'dismissed_backdrop' | 'engaged' | 'submitted',
  pageType?: string
): void {
  safeGtag('event', 'exit_intent', {
    event_category: action === 'submitted' ? 'conversion' : 'engagement',
    exit_intent_action: action,
    page_type: pageType,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    ...(action === 'submitted' && { currency: 'USD', value: 1 }),
  });

  // Clarity: Track exit intent behavior
  claritySet('exit_intent', action);
  clarityEvent(`exit_intent_${action}`);

  if (action === 'submitted') {
    clarityUpgrade('exit_intent_conversion');
  }
}

// ============================================
// TOOL RESULT TRACKING
// ============================================

/**
 * Track actions taken on tool results
 * Shows engagement depth with tools
 */
export function trackToolResultAction(
  toolSlug: string,
  action: 'copy' | 'export_pdf' | 'export_csv' | 'export_json' | 'share' | 'download' | 'print' | 'save'
): void {
  safeGtag('event', 'tool_result_action', {
    event_category: 'micro_conversion',
    tool_slug: toolSlug,
    result_action: action,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
  });

  // Clarity: Tag users who export/copy tool results
  claritySet('tool_result_action', action);
  if (action.startsWith('export_') || action === 'download') {
    claritySet('tool_exporter', 'true');
    clarityEvent('tool_export');
  }
}

// ============================================
// CHAT WIDGET TRACKING
// ============================================

/**
 * Track chat conversation metrics
 * Understand chat engagement quality
 */
export function trackChatSession(
  action: 'started' | 'message_sent' | 'ended',
  metrics?: {
    messageCount?: number;
    sessionDurationSeconds?: number;
    userMessageCount?: number;
    botMessageCount?: number;
  }
): void {
  safeGtag('event', 'chat_session', {
    event_category: 'engagement',
    chat_action: action,
    message_count: metrics?.messageCount,
    session_duration: metrics?.sessionDurationSeconds,
    user_messages: metrics?.userMessageCount,
    bot_messages: metrics?.botMessageCount,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
  });

  // Clarity: Tag chat users
  claritySet('chat_user', 'true');
  clarityEvent(`chat_${action}`);

  if (action === 'started') {
    clarityUpgrade('chat_engagement');
  }

  // Tag engaged chatters (3+ messages)
  if (action === 'ended' && metrics?.userMessageCount && metrics.userMessageCount >= 3) {
    claritySet('chat_engaged', 'true');
  }
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Initialize scroll depth tracking
 * Call this on pages where you want to track scroll behavior
 */
export function initScrollTracking(): () => void {
  if (typeof window === 'undefined') return () => {};

  const thresholds = [25, 50, 75, 90];
  const tracked = new Set<number>();

  const handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;
    const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

    thresholds.forEach(threshold => {
      if (scrollPercent >= threshold && !tracked.has(threshold)) {
        tracked.add(threshold);
        trackScrollDepth(threshold);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => window.removeEventListener('scroll', handleScroll);
}

/**
 * Initialize time-on-page tracking
 * Call this on content pages to track engagement
 */
export function initTimeTracking(
  contentType: 'blog' | 'tool' | 'knowledge_base' | 'service',
  slug: string
): () => void {
  if (typeof window === 'undefined') return () => {};

  const startTime = Date.now();
  const engagementThresholds = [30, 60, 120, 300]; // seconds
  const tracked = new Set<number>();

  const checkEngagement = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);

    engagementThresholds.forEach(threshold => {
      if (timeSpent >= threshold && !tracked.has(threshold)) {
        tracked.add(threshold);
        trackContentEngagement(contentType, slug, threshold);
      }
    });
  };

  const interval = setInterval(checkEngagement, 10000); // Check every 10 seconds

  return () => clearInterval(interval);
}

// ============================================
// CONVERSION ATTRIBUTION TRACKING
// ============================================

const LANDING_PAGE_KEY = 'ihq_landing_page';
const VISIT_COUNT_KEY = 'ihq_visit_count';
const FIRST_VISIT_KEY = 'ihq_first_visit';
const VISITOR_ID_KEY = 'ihq_visitor_id';

/**
 * Initialize visitor tracking on page load
 * Captures landing page, visit count, and visitor ID
 * Call this once in the root layout
 */
export function initVisitorTracking(): void {
  if (typeof window === 'undefined') return;

  // Generate or retrieve visitor ID (persists across sessions)
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }

  // Track first visit date
  if (!localStorage.getItem(FIRST_VISIT_KEY)) {
    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
  }

  // Increment visit count (per session)
  if (!sessionStorage.getItem('ihq_session_started')) {
    sessionStorage.setItem('ihq_session_started', 'true');
    const visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, visitCount.toString());

    // Track return visitors in Clarity
    if (visitCount > 1) {
      claritySet('return_visitor', 'true');
      claritySet('visit_number', visitCount.toString());
    }

    // GA4: Track new vs returning
    safeGtag('event', 'session_start_custom', {
      event_category: 'engagement',
      visit_number: visitCount,
      is_return_visitor: visitCount > 1,
      days_since_first_visit: getDaysSinceFirstVisit(),
    });
  }

  // Store landing page (only on first page of session)
  if (!sessionStorage.getItem(LANDING_PAGE_KEY)) {
    const landingPage = window.location.pathname;
    sessionStorage.setItem(LANDING_PAGE_KEY, landingPage);

    // Also store referrer
    if (document.referrer) {
      sessionStorage.setItem('ihq_referrer', document.referrer);
    }

    // Tag in Clarity
    claritySet('landing_page', landingPage);
  }
}

/**
 * Get the landing page for this session (for attribution)
 */
export function getLandingPage(): string {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(LANDING_PAGE_KEY) || '';
}

/**
 * Get visit count for this visitor
 */
export function getVisitCount(): number {
  if (typeof window === 'undefined') return 1;
  return parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '1', 10);
}

/**
 * Get days since first visit
 */
function getDaysSinceFirstVisit(): number {
  if (typeof window === 'undefined') return 0;
  const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
  if (!firstVisit) return 0;
  const daysDiff = (Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24);
  return Math.floor(daysDiff);
}

/**
 * Enhanced lead form submission with attribution data
 * Includes landing page, visit count, and days to convert
 */
export function trackLeadWithAttribution(
  formName: string,
  formLocation: string,
  email?: string
): void {
  const landingPage = getLandingPage();
  const visitCount = getVisitCount();
  const daysToConvert = getDaysSinceFirstVisit();
  const referrer = sessionStorage.getItem('ihq_referrer') || '';

  // GA4: Enhanced lead tracking with attribution
  safeGtag('event', 'generate_lead', {
    event_category: 'conversion',
    form_name: formName,
    form_location: formLocation,
    landing_page: landingPage,
    visit_count: visitCount,
    days_to_convert: daysToConvert,
    referrer_domain: referrer ? (() => { try { return new URL(referrer).hostname; } catch { return 'direct'; } })() : 'direct',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    currency: 'USD',
    value: 1,
  });

  // Clarity: Full attribution tagging
  claritySet('lead_submitted', 'true');
  claritySet('lead_form', formName);
  claritySet('lead_landing_page', landingPage);
  claritySet('lead_visit_count', visitCount.toString());
  claritySet('lead_days_to_convert', daysToConvert.toString());
  clarityEvent('lead_form_submitted');
  clarityUpgrade('lead_submission');

  if (email) {
    clarityIdentify(email);
  }
}

// ============================================
// CTA VISIBILITY TRACKING
// ============================================

/**
 * Track when a CTA element becomes visible in the viewport
 * Uses Intersection Observer for efficient tracking
 * @param elementId - ID of the CTA element to track
 * @param ctaName - Name for tracking (e.g., "hero_cta", "footer_cta")
 */
export function trackCtaVisibility(elementId: string, ctaName: string): () => void {
  if (typeof window === 'undefined') return () => {};

  const element = document.getElementById(elementId);
  if (!element) return () => {};

  let hasBeenVisible = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          hasBeenVisible = true;

          safeGtag('event', 'cta_visible', {
            event_category: 'engagement',
            cta_name: ctaName,
            cta_element_id: elementId,
            page_url: typeof window !== 'undefined' ? window.location.href : '',
          });

          claritySet('cta_seen', ctaName);
          clarityEvent(`cta_visible_${ctaName}`);
        }
      });
    },
    { threshold: 0.5 } // 50% of element must be visible
  );

  observer.observe(element);

  return () => observer.disconnect();
}

/**
 * Initialize CTA visibility tracking for common CTA sections
 * Call this in page layouts
 */
export function initCtaVisibilityTracking(): () => void {
  if (typeof window === 'undefined') return () => {};

  const cleanupFns: (() => void)[] = [];

  // Wait for DOM to be ready
  const init = () => {
    // Track common CTA sections by ID
    const ctaSelectors = [
      { id: 'contact-cta', name: 'contact_section' },
      { id: 'cta-section', name: 'main_cta' },
      { id: 'hero-cta', name: 'hero_cta' },
      { id: 'footer-cta', name: 'footer_cta' },
      { id: 'inline-cta', name: 'inline_cta' },
    ];

    ctaSelectors.forEach(({ id, name }) => {
      const cleanup = trackCtaVisibility(id, name);
      cleanupFns.push(cleanup);
    });
  };

  // Run after a short delay to ensure DOM is ready
  setTimeout(init, 100);

  return () => cleanupFns.forEach(fn => fn());
}

// ============================================
// INTERNAL SEARCH TRACKING
// ============================================

/**
 * Track internal site search with enhanced data
 */
export function trackInternalSearch(
  query: string,
  resultsCount: number,
  searchLocation: 'header' | 'page' | 'command_palette' = 'header'
): void {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return;

  safeGtag('event', 'view_search_results', {
    event_category: 'engagement',
    search_term: trimmedQuery,
    results_count: resultsCount,
    search_location: searchLocation,
    has_results: resultsCount > 0,
  });

  // Track no-results searches separately (reveals content gaps)
  if (resultsCount === 0) {
    safeGtag('event', 'search_no_results', {
      event_category: 'engagement',
      search_term: trimmedQuery,
      search_location: searchLocation,
    });

    claritySet('search_no_results', trimmedQuery);
    clarityEvent('search_no_results');
  }

  // Tag searchers in Clarity
  claritySet('site_searcher', 'true');
  claritySet('last_search', trimmedQuery);
}

/**
 * Track search result click
 */
export function trackInternalSearchClick(
  query: string,
  clickedUrl: string,
  position: number,
  resultType: 'tool' | 'blog' | 'service' | 'kb' | 'glossary'
): void {
  safeGtag('event', 'select_content', {
    event_category: 'engagement',
    content_type: 'search_result',
    search_term: query,
    clicked_url: clickedUrl,
    position: position,
    result_type: resultType,
  });
}

// ============================================
// SCROLL-TO-CTA TRACKING
// ============================================

/**
 * Track if user scrolled far enough to see the contact/CTA section
 * Different from general scroll depth - specifically tracks CTA visibility via scroll
 */
export function initScrollToCtaTracking(ctaSelector: string = '#contact-cta, #cta-section, [data-cta-section]'): () => void {
  if (typeof window === 'undefined') return () => {};

  let hasReachedCta = false;

  const checkCtaVisibility = () => {
    if (hasReachedCta) return;

    const ctaElements = document.querySelectorAll(ctaSelector);
    if (ctaElements.length === 0) return;

    const viewportBottom = window.scrollY + window.innerHeight;

    ctaElements.forEach((element) => {
      const elementTop = (element as HTMLElement).offsetTop;
      if (viewportBottom >= elementTop && !hasReachedCta) {
        hasReachedCta = true;

        safeGtag('event', 'scroll_to_cta', {
          event_category: 'engagement',
          cta_reached: true,
          scroll_depth_at_cta: Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100),
        });

        claritySet('scrolled_to_cta', 'true');
        clarityEvent('scroll_to_cta');
      }
    });
  };

  window.addEventListener('scroll', checkCtaVisibility, { passive: true });
  // Check immediately in case CTA is above the fold
  checkCtaVisibility();

  return () => window.removeEventListener('scroll', checkCtaVisibility);
}

// ============================================
// PAGE EXIT TRACKING
// ============================================

/**
 * Track page exit behavior - where did they go?
 * Helps understand if they're exploring or bouncing
 */
export function trackPageExit(
  exitType: 'internal_link' | 'external_link' | 'back_button' | 'close_tab',
  destinationUrl?: string
): void {
  const timeOnPage = performance.now() / 1000; // seconds

  safeGtag('event', 'page_exit', {
    event_category: 'engagement',
    exit_type: exitType,
    destination_url: destinationUrl,
    time_on_page: Math.round(timeOnPage),
    scroll_depth_at_exit: getScrollDepth(),
  });
}

/**
 * Get current scroll depth percentage
 */
function getScrollDepth(): number {
  if (typeof window === 'undefined') return 0;
  const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollHeight <= 0) return 100;
  return Math.round((window.scrollY / scrollHeight) * 100);
}
